import { useContext } from 'react'

import { datadogLogs } from '@datadog/browser-logs'
import { OptimizelyContext } from '@optimizely/react-sdk'
import Cookies from 'js-cookie'

import { getAuthToken } from '@library/auth'
import { canUseWindow } from '@library/environment'

/**
 * Feature flag hook to check if Value Comprehension experiment is enabled
 * Uses Optimizely experiment: beetroots_ofx_value_comprehension
 *
 * @returns {boolean} true if Value Comprehension experiment should be shown
 */
export const useIsValueComprehensionEnabled = (): boolean => {
  const { optimizely } = useContext(OptimizelyContext)

  // Return false early if not in browser environment to avoid server-side execution
  if (!canUseWindow()) {
    return false
  }

  const authToken = getAuthToken()
  const isAuthenticated = !!authToken

  let hasAcceptedAnalytics = false
  try {
    const consentCookieString = Cookies.get('v1_gousto_cookie_consent')
    const consentCookie = consentCookieString ? JSON.parse(consentCookieString) : {}
    hasAcceptedAnalytics = consentCookie?.categories?.includes('ANALYTICS')
  } catch (error) {
    let errorMessage = 'Unknown error when parsing cookie consent'
    if (error instanceof Error) {
      errorMessage = error.message
      datadogLogs.logger.error('Error parsing ValueComprehension cookie consent', {
        error: errorMessage,
        cookieConsent: Cookies.get('v1_gousto_cookie_consent'),
      })
    }
  }

  // We only allocate users to the experiment if they have consented
  // to the Analytics cookies and are not authenticated (unless bypassed)
  const { enabled, variationKey } =
    optimizely && hasAcceptedAnalytics && !isAuthenticated
      ? optimizely.decide('beetroots_ofx_value_comprehension')
      : { enabled: false, variationKey: undefined }

  const isEnabled = enabled && variationKey === 't1'

  return isEnabled
}
